import * as React from "react";
import { Input } from "@/components/ui/input";

type CommonProps = {
  value: number | null | undefined;
  onValueChange: (n: number) => void;
  min?: number;
  max?: number;
  step?: number | string;
  allowEmpty?: boolean; // if true, empty stays as 0 without clamping; default true (empty = don't fire until valid)
  emptyValue?: number; // value emitted on blur if left empty (default: min ?? 0)
};

function clamp(n: number, min?: number, max?: number) {
  if (min !== undefined && n < min) n = min;
  if (max !== undefined && n > max) n = max;
  return n;
}

function useNumberInputState({
  value,
  onValueChange,
  min,
  max,
  emptyValue,
}: CommonProps) {
  const [display, setDisplay] = React.useState<string>(
    value === null || value === undefined ? "" : String(value),
  );
  const focused = React.useRef(false);

  // Sync external value in when not actively editing.
  React.useEffect(() => {
    if (focused.current) return;
    const next = value === null || value === undefined ? "" : String(value);
    setDisplay((prev) => (prev === next ? prev : next));
  }, [value]);

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    setDisplay(v);
    if (v === "" || v === "-" || v === "." || v === "-.") return; // wait for a valid number
    const n = Number(v);
    if (!Number.isNaN(n)) onValueChange(n);
  };

  const onFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    focused.current = true;
    e.currentTarget.select();
  };

  const onBlur = () => {
    focused.current = false;
    if (display === "" || display === "-" || display === "." || display === "-.") {
      const fallback = emptyValue ?? min ?? 0;
      setDisplay(String(fallback));
      onValueChange(fallback);
      return;
    }
    const n = Number(display);
    if (Number.isNaN(n)) {
      const fallback = emptyValue ?? min ?? 0;
      setDisplay(String(fallback));
      onValueChange(fallback);
      return;
    }
    const clamped = clamp(n, min, max);
    if (clamped !== n) {
      setDisplay(String(clamped));
      onValueChange(clamped);
    }
  };

  return { display, onChange, onFocus, onBlur };
}

type ShadProps = CommonProps &
  Omit<React.InputHTMLAttributes<HTMLInputElement>, "value" | "onChange" | "min" | "max" | "step" | "type">;

export const NumberInput = React.forwardRef<HTMLInputElement, ShadProps>(
  function NumberInput(props, ref) {
    const { value, onValueChange, min, max, step, allowEmpty, emptyValue, ...rest } = props;
    const state = useNumberInputState({ value, onValueChange, min, max, emptyValue });
    return (
      <Input
        ref={ref}
        type="number"
        inputMode="decimal"
        min={min}
        max={max}
        step={step}
        value={state.display}
        onChange={state.onChange}
        onFocus={state.onFocus}
        onBlur={state.onBlur}
        {...rest}
      />
    );
  },
);

export const NumberInputRaw = React.forwardRef<HTMLInputElement, ShadProps>(
  function NumberInputRaw(props, ref) {
    const { value, onValueChange, min, max, step, allowEmpty, emptyValue, ...rest } = props;
    const state = useNumberInputState({ value, onValueChange, min, max, emptyValue });
    return (
      <input
        ref={ref}
        type="number"
        inputMode="decimal"
        min={min}
        max={max}
        step={step}
        value={state.display}
        onChange={state.onChange}
        onFocus={state.onFocus}
        onBlur={state.onBlur}
        {...rest}
      />
    );
  },
);
